import { ISection } from 'store/modules/goal/types'
import { buildUsersSelectionFromLastActiveGoal } from 'utils/goalSellerSelection'

import {
  buildGoalSections,
  derivePeriodTypeFromSections,
  getSundaysInMonth,
  normalizeSectionsToMonth,
  StoreLastActiveData
} from '../service'
import { BulkStore, StoreGoalConfig } from '../types'
import {
  buildMonthGoalTemplates,
  updateStoreTotal
} from '../utils'

export type CommissionConfigField = 'directCommission' | 'distribute' | 'periodType' | 'splitCommission' | 'storeSold'
export type CommissionConfigFieldValue = boolean | 'single' | 'multiple' | null
export type RemunerationConfigField = 'salaryBase' | 'dsr' | 'price' | 'ticket' | 'total'

const remunerationConfigFieldMap = {
  dsr: 'dsr',
  price: 'price',
  salaryBase: 'salaryBase',
  ticket: 'ticket',
  total: 'total'
} as const

export const applyCommissionFieldChange = (
  config: StoreGoalConfig,
  selectedMonth: string,
  field: CommissionConfigField,
  value: CommissionConfigFieldValue
) => {
  const nextConfig = { ...config, [field]: value }
  if (field === 'periodType') {
    nextConfig.splitCommission = value === 'single' ? false : nextConfig.splitCommission
    nextConfig.sections = buildGoalSections(selectedMonth, value as 'single' | 'multiple')
  }
  return nextConfig
}

const applyDsrToUsers = (users: StoreGoalConfig['usersWorking'], value: number | null) =>
  users.map(user => ({ ...user, dsr: Number(value || 0) > 0 }))

export const applyValueFieldChange = (
  config: StoreGoalConfig,
  field: RemunerationConfigField,
  value: number | null
): StoreGoalConfig => {
  if (field === 'total') return updateStoreTotal(config, value)
  if (field === 'dsr') {
    return {
      ...config,
      dsr: value,
      usersNotWorking: applyDsrToUsers(config.usersNotWorking, value),
      usersWorking: applyDsrToUsers(config.usersWorking, value)
    }
  }
  return { ...config, [remunerationConfigFieldMap[field]]: value }
}

export const applyCommissionData = (
  config: StoreGoalConfig,
  selectedMonth: string,
  data: StoreLastActiveData
): StoreGoalConfig => {
  const periodType = derivePeriodTypeFromSections(data.commissionConfig?.sections || [])
  const sections = normalizeSectionsToMonth(
    data.commissionConfig?.sections as ISection[] | undefined,
    selectedMonth,
    periodType || 'single'
  )

  return {
    ...config,
    directCommission: data.commissionConfig?.directCommission ?? false,
    distribute: data.commissionConfig?.distribute ?? null,
    periodType,
    sections,
    splitCommission: periodType === 'single' ? false : data.commissionConfig?.splitCommission ?? null,
    storeSold: data.commissionConfig?.storeSold ?? null
  }
}

export const applyValueData = (
  config: StoreGoalConfig,
  selectedMonth: string,
  data: StoreLastActiveData
): StoreGoalConfig => updateStoreTotal({
  ...config,
  dsr: data.valueConfig?.dsr ?? getSundaysInMonth(selectedMonth),
  inheritedSalaryComplement: data.commissionConfig?.directCommission === true
    ? data.valueConfig?.complement ?? false
    : false,
  price: data.valueConfig?.price ?? null,
  salaryBase: data.valueConfig?.base ?? null,
  ticket: data.valueConfig?.ticket ?? null
}, data.valueConfig?.total ?? null)

export const applySellerGoalData = (
  config: StoreGoalConfig,
  store: BulkStore,
  selectedMonth: string,
  data: StoreLastActiveData | undefined
): StoreGoalConfig => {
  const dsr = config.dsr ?? getSundaysInMonth(selectedMonth)
  const usersSelection = buildUsersSelectionFromLastActiveGoal(
    store,
    data ? data.sellerGoalConfig?.usersWorking || [] : undefined,
    dsr
  )
  const monthGoalTemplates = buildMonthGoalTemplates(data?.sellerGoalConfig?.monthGoals || [])
  return {
    ...config,
    fixedCommission: Number(((data?.sellerGoalConfig?.fixedCommission || 0) * 100).toFixed(2)),
    hasFixedCommission: Boolean(data?.sellerGoalConfig?.fixedCommission),
    monthGoalTemplates,
    sellerGoalDataLoaded: true,
    storeName: store.name,
    usersNotWorking: usersSelection.usersNotWorking,
    usersWorking: usersSelection.usersWorking
  }
}
